import type { Load, RoutePreference, RoutePreview, Stop, StopInput, StopKind } from './api';
import { fromLocalInput, toLocalInput } from './format';

/** Most stops a load takes, the first pickup and the last delivery included (echo's limit too) */
export const MAX_STOPS = 20;

/** A stop as the dialog edits it: the time is a datetime-local value, '' while not set; found once it has a point */
export interface StopDraft {
  kind: StopKind;
  place: string;
  at: string;
  lat: number | null;
  lon: number | null;
  /** The point was picked (a suggestion or the map): echo routes it as it is */
  pinned: boolean;
  /** Stop 1's time is half typed: the browser reports it empty, but it is not left empty on purpose */
  startIncomplete?: boolean;
}

/** A row with no place and no point */
const emptyRow = (kind: StopKind): StopDraft => ({ kind, place: '', at: '', lat: null, lon: null, pinned: false });

/** The dialog's rows: a load's stops, or an empty pickup and delivery for a new load */
export function draftsOf(load: Pick<Load, 'stops'> | null): StopDraft[] {
  if (!load) return [emptyRow('pickup'), emptyRow('delivery')];
  // Only the start is typed; the other times are worked out
  return load.stops.map((stop, index) => ({
    kind: stop.kind,
    place: stop.place,
    at: index === 0 && stop.at ? toLocalInput(stop.at) : '',
    lat: stop.lat,
    lon: stop.lon,
    pinned: stop.pinned,
  }));
}

/** A new row before the last one, of the same kind as the row above it; unchanged at MAX_STOPS */
export function insertStop(drafts: StopDraft[]): StopDraft[] {
  if (drafts.length >= MAX_STOPS) return drafts;
  const last = drafts.length - 1;
  return [...drafts.slice(0, last), emptyRow(drafts[last - 1].kind), drafts[last]];
}

/** A middle row one step up or down; the first and last rows stay where they are */
export function moveStop(drafts: StopDraft[], index: number, step: -1 | 1): StopDraft[] {
  const to = index + step;
  const middle = (at: number) => at >= 1 && at <= drafts.length - 2;
  if (!middle(index) || !middle(to)) return drafts;
  const next = [...drafts];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

/** Drops a middle row; the first and last rows always stay */
export function removeStop(drafts: StopDraft[], index: number): StopDraft[] {
  if (index < 1 || index > drafts.length - 2) return drafts;
  return drafts.filter((_, at) => at !== index);
}

export type StopProblem = { stop: number; reason: 'place' | 'found' | 'start' };

/** The first thing wrong with the rows, by stop number (from 1), or null: a place left empty, a place not found, or a half-typed start (an empty one is fine) */
export function checkStops(drafts: StopDraft[], context: PlaceContext = {}): StopProblem | null {
  const empty = drafts.findIndex((draft) => !draft.place.trim());
  if (empty >= 0) return { stop: empty + 1, reason: 'place' };
  const lost = drafts.findIndex((draft) => !counts(draft, context));
  if (lost >= 0) return { stop: lost + 1, reason: 'found' };
  if (drafts[0].startIncomplete) return { stop: 1, reason: 'start' };
  return null;
}

/** The rows as echo takes them */
export const stopsInput = (drafts: StopDraft[]): StopInput[] =>
  drafts.map((draft, index) => ({
    kind: draft.kind,
    place: draft.place.trim(),
    at: index === 0 ? fromLocalInput(draft.at) : null,
    // Only picked points go back: an old geocoded stop stays a name, so nothing re-routes
    ...(draft.pinned && draft.lat !== null && draft.lon !== null ? { lat: draft.lat, lon: draft.lon } : {}),
  }));

type PlaceLike = { place: string; lat: number | null; lon: number | null; pinned: boolean };

/** A stop's key as echo works it out: the place, and its point when pinned */
const draftKey = (stop: PlaceLike): string => {
  const place = stop.place.trim().toLowerCase();
  return stop.pinned && stop.lat !== null && stop.lon !== null ? `${place}@${stop.lat.toFixed(5)},${stop.lon.toFixed(5)}` : place;
};

/** Whether the rows still name the saved places in the saved order (case and spaces aside, pinned points included) */
export const samePlaces = (drafts: PlaceLike[], stops: PlaceLike[]): boolean =>
  drafts.length === stops.length && drafts.every((draft, index) => draftKey(draft) === draftKey(stops[index]));

/** A stop is found once it has a point: picked (pinned), or geocoded earlier by echo */
export const found = (draft: StopDraft): boolean => draft.lat !== null && draft.lon !== null;

/** What else lets a row without a point stand: the load's saved stops, and place search being off */
export interface PlaceContext {
  saved?: PlaceLike[];
  /** Echo has no ORS key: nothing can be picked, so a typed place stands (the estimate carries the times) */
  searchOff?: boolean;
}

/** Found, or a saved place echo finds itself (a load from before picking, or one whose route was not found), or typed while search is off */
const counts = (draft: StopDraft, { saved = [], searchOff = false }: PlaceContext): boolean =>
  found(draft) || saved.some((stop) => draftKey(stop) === draftKey(draft)) || (searchOff && draft.place.trim() !== '');
export const allFound = (drafts: StopDraft[], context: PlaceContext = {}): boolean => drafts.every((draft) => counts(draft, context));

/** The place column takes 120 characters; ORS labels can be longer */
export const shortenLabel = (label: string): string => (label.length > 120 ? `${label.slice(0, 119)}…` : label);

/** Typing a place drops its point: it is found again only by a new pick */
export const typed = (draft: StopDraft, place: string): StopDraft => ({ ...draft, place, lat: null, lon: null, pinned: false });

/** A suggestion picked: its label and its point, pinned */
export const picked = (draft: StopDraft, hit: { label: string; lat: number; lon: number }): StopDraft => ({ ...draft, place: shortenLabel(hit.label), lat: hit.lat, lon: hit.lon, pinned: true });

/** A point picked on the map: named by reverse geocoding, else by its coordinates */
export const pinAt = (draft: StopDraft, lat: number, lon: number, label: string | null): StopDraft =>
  picked(draft, { label: label ?? `${lat.toFixed(5)}, ${lon.toFixed(5)}`, lat, lon });

/** The rows as the route preview takes them: pinned ones with their point */
export const routePoints = (drafts: StopDraft[]): Array<{ place: string; lat?: number; lon?: number }> =>
  drafts.map((draft) => (draft.pinned && draft.lat !== null && draft.lon !== null ? { place: draft.place.trim(), lat: draft.lat, lon: draft.lon } : { place: draft.place.trim() }));

/** ORS's encoded polyline (precision 5) as [lat, lon] pairs */
export function decodePolyline(encoded: string, precision = 5): Array<[number, number]> {
  const factor = 10 ** precision;
  const points: Array<[number, number]> = [];
  let index = 0;
  let lat = 0;
  let lon = 0;
  while (index < encoded.length) {
    for (const axis of [0, 1]) {
      let result = 0;
      let shift = 0;
      let byte: number;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);
      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (axis === 0) lat += delta;
      else lon += delta;
    }
    points.push([lat / factor, lon / factor]);
  }
  return points;
}

/** How many stops lie between the first and the last */
export const stopsBetween = (stops: Stop[]): number => Math.max(0, stops.length - 2);

/** Every place a load passes, for search */
export const stopsText = (stops: Stop[]): string => stops.map((stop) => stop.place).join(' ');

/** The longest estimate echo takes: 14 days */
const MAX_ESTIMATE_S = 14 * 24 * 3600;

/** The dialog's estimate fields as seconds: null when both are empty, undefined when they make no sense */
export function secondsOf(hours: string, minutes: string): number | null | undefined {
  if (hours.trim() === '' && minutes.trim() === '') return null;
  const h = hours.trim() === '' ? 0 : Number(hours);
  const m = minutes.trim() === '' ? 0 : Number(minutes);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || m < 0 || m > 59) return undefined;
  const seconds = h * 3600 + m * 60;
  return seconds >= 60 && seconds <= MAX_ESTIMATE_S ? seconds : undefined;
}

export function hoursMinutesOf(seconds: number | null): { hours: string; minutes: string } {
  if (seconds === null) return { hours: '', minutes: '' };
  return { hours: String(Math.floor(seconds / 3600)), minutes: String(Math.round((seconds % 3600) / 60)) };
}

/** echo's planTimes on ISO strings, for the dialog's live times (echo's answer after saving is the final word) */
export function planTimes(startIso: string, legsS: Array<number | null>, driveS: number | null, estimateS: number | null): Array<string | null> | null {
  const duration = estimateS ?? driveS;
  if (duration === null) return null;
  const start = Date.parse(startIso);
  const last = legsS.length - 1;
  const legsKnown = driveS !== null && legsS.slice(1).every((leg) => leg !== null);
  let driven = 0;
  return legsS.map((leg, index) => {
    if (index === 0) return startIso;
    if (index === last) return new Date(start + duration * 1000).toISOString();
    if (!legsKnown) return null;
    driven += leg!;
    // Every place the same: no drive to share out, so the stops between are at the start
    const share = driveS! > 0 ? driven / driveS! : 0;
    return new Date(start + Math.round(share * duration) * 1000).toISOString();
  });
}

/** A route the dialog can plan by: km, drive, and each leg's drive (null when unknown) */
export interface KnownRoute {
  distanceM: number;
  driveS: number;
  legsS: Array<number | null>;
}

/**
 * The route for the rows and the chosen option: the live preview's when it has it, else the load's
 * saved route while the rows and the option are the saved ones (an ORS outage never blocks editing a
 * routed load). The saved view has no legs, so they are read back from its stop times:
 * (time − start) × drive ÷ its duration.
 */
export function knownRoute(preview: RoutePreview | undefined, load: Load | null, drafts: StopDraft[], preference: RoutePreference): KnownRoute | null {
  const option = preview?.status === 'ok' ? preview.options.find((candidate) => candidate.preference === preference) : undefined;
  if (option) return { distanceM: option.distanceM, driveS: option.driveS, legsS: option.legsS ?? drafts.slice(1).map(() => null) };
  if (!load || load.route !== 'ok' || load.routePreference !== preference || load.driveS === null || load.distanceM === null || !samePlaces(drafts, load.stops)) return null;
  const driveS = load.driveS;
  const start = Date.parse(load.stops[0].at!);
  const span = (Date.parse(load.stops[load.stops.length - 1].at!) - start) / 1000;
  const known = span > 0 && load.stops.every((stop) => stop.at !== null);
  const reached = load.stops.map((stop, index) => (index === load.stops.length - 1 ? driveS : Math.round(((Date.parse(stop.at!) - start) / 1000) * (driveS / span))));
  return {
    distanceM: load.distanceM,
    driveS,
    legsS: load.stops.slice(1).map((_, index) => (known ? reached[index + 1] - reached[index] : null)),
  };
}

/**
 * An ORS answer worth keeping: an unavailable one throws instead, so the query retries it and never
 * keeps it (echo does not cache it either); typing the same text again then asks again
 */
export function usable<T extends { status: string }>(answer: T): T {
  if (answer.status === 'unavailable') throw new Error('The routing service is unavailable');
  return answer;
}

/** The route options the dialog offers, in card order. Najkraća is not offered: nothing stands in for it while ORS is down */
export const ROUTE_PREFERENCES: RoutePreference[] = ['fastest', 'no_tolls'];

/** The options a preview asks for: every option the dialog offers, so both cards show their km and drive */
export const previewOptions = (): RoutePreference[] => ROUTE_PREFERENCES;

/** Route and place lookups are asked once more at most: every retry costs ORS or LocationIQ calls again */
export const retriesLeft = (failureCount: number): boolean => failureCount < 1;

/** The estimate a save sends: only while Moja procena is switched on; off means the route's drive */
export const estimateOf = (on: boolean, fields: { hours: string; minutes: string }): number | null | undefined =>
  on ? secondsOf(fields.hours, fields.minutes) : null;

/** Saving needs a duration only to time the stops: with a start, and neither an estimate nor a route */
export const needsDuration = (estimateS: number | null | undefined, routed: KnownRoute | null, startIso: string | null): boolean =>
  estimateS === null && routed === null && startIso !== null;
